import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Compass, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/* Brand tokens — kept inline to match Landing/dark pages */
const C = {
  bg: "#050505",
  surface: "#0d0d0d",
  border: "#1f1f1f",
  borderHi: "#2a2a2a",
  teal: "#12cfc0",
  tealLt: "#58e8de",
  text: "#f5f5f5",
  textHi: "#ffffff",
  sub: "#a1a1aa",
};

function Logo() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: `linear-gradient(135deg, ${C.teal}, ${C.tealLt})`,
          display: "grid",
          placeItems: "center",
          color: "#031518",
          fontWeight: 900,
          fontFamily: "Outfit, system-ui, sans-serif",
          fontSize: 18,
          boxShadow: `0 6px 22px -10px ${C.teal}`,
        }}
      >
        C
      </div>
      <span
        style={{
          color: C.textHi,
          fontFamily: "Outfit, system-ui, sans-serif",
          fontWeight: 800,
          letterSpacing: 0.4,
          fontSize: 18,
        }}
      >
        Coach AI
      </span>
    </div>
  );
}

const NotFound = () => {
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname,
    );
  }, [location.pathname]);

  const homeTo = user ? "/app" : "/";
  const homeLabel = user ? "Back to dashboard" : "Back to homepage";

  return (
    <>
      <Helmet>
        <title>Page not found · Coach AI</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div
        style={{
          minHeight: "100vh",
          background: `radial-gradient(1200px 600px at 50% -10%, ${C.teal}10, transparent 60%), ${C.bg}`,
          color: C.text,
          fontFamily: "DM Sans, system-ui, sans-serif",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <header
          style={{
            padding: "20px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            maxWidth: 1200,
            width: "100%",
            margin: "0 auto",
          }}
        >
          <Link to="/" style={{ textDecoration: "none" }}>
            <Logo />
          </Link>
        </header>

        <main
          style={{
            flex: 1,
            display: "grid",
            placeItems: "center",
            padding: "24px",
          }}
        >
          <div
            style={{
              maxWidth: 520,
              width: "100%",
              textAlign: "center",
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 18,
              padding: "44px 28px",
              boxShadow: "0 30px 60px -30px rgba(0,0,0,0.6)",
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                margin: "0 auto 20px",
                borderRadius: 16,
                background: `${C.teal}18`,
                border: `1px solid ${C.teal}40`,
                display: "grid",
                placeItems: "center",
                color: C.teal,
              }}
            >
              <Compass size={30} strokeWidth={2.2} />
            </div>

            <p
              style={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 2,
                color: C.sub,
                textTransform: "uppercase",
                margin: 0,
                marginBottom: 8,
              }}
            >
              Error 404
            </p>
            <h1
              style={{
                fontFamily: "Outfit, system-ui, sans-serif",
                fontWeight: 800,
                fontSize: 32,
                color: C.textHi,
                margin: 0,
                marginBottom: 12,
                letterSpacing: -0.4,
              }}
            >
              Page not found
            </h1>
            <p
              style={{
                color: C.sub,
                fontSize: 15,
                lineHeight: 1.55,
                margin: 0,
                marginBottom: 24,
              }}
            >
              We couldn't find the page you were looking for. It may have moved or
              never existed. Let's get you back on track.
              <span
                style={{
                  display: "block",
                  marginTop: 10,
                  fontSize: 12,
                  opacity: 0.65,
                }}
              >
                Requested URL:{" "}
                <code style={{ color: C.tealLt }}>{location.pathname}</code>
              </span>
            </p>

            <Link
              to={homeTo}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "12px 22px",
                borderRadius: 12,
                background: `linear-gradient(135deg, ${C.teal}, ${C.tealLt})`,
                color: "#031518",
                fontWeight: 700,
                fontFamily: "Outfit, system-ui, sans-serif",
                textDecoration: "none",
                boxShadow: `0 10px 30px -12px ${C.teal}`,
              }}
            >
              {homeLabel}
              <ArrowRight size={18} />
            </Link>

            <div style={{ marginTop: 18 }}>
              <Link
                to="/"
                style={{
                  color: C.sub,
                  fontSize: 13,
                  textDecoration: "none",
                  borderBottom: `1px dashed ${C.borderHi}`,
                  paddingBottom: 1,
                }}
              >
                Or return to the homepage
              </Link>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default NotFound;
